import {canUseCustomerBranding, type Plan} from "home/gating";
import {RECIPIENT_PATH_PATTERN} from "attribution/adAttribution";
import {treeRelativePath} from "seo/documentLang";
import {isBrandColor, normalizeBrandColor} from "./brandColors";

/**
 * The colour the signed-in app is themed with, or `null` for today's look (kitecorp/transferify-ui#2935).
 * React-free by mandate (epic #26).
 *
 * Branding is Premium, and the api gates it with `customerBrandingAllowedFor`. Its READ of the brand
 * does not re-check the plan, so an account that has left Premium still gets its saved colour back: the
 * plan is checked HERE, on every render, which is also what drops the colour the moment the account
 * refresh reports the plan lapsed.
 *
 * A recipient page is never themed: it wears the SENDER's brand, so a signed-in Premium account opening
 * someone else's link must not paint its own colour over theirs.
 */
export function accountAppColor({authenticated, plan, primaryColor, pathname}: AccountAppColorInput): string | null {
	if (!shouldLoadAccountAppColor({authenticated, plan}) || !isBrandColor(primaryColor)
		|| RECIPIENT_PATH_PATTERN.test(treeRelativePath(pathname))) {
		return null;
	}
	return normalizeBrandColor(primaryColor);
}

/**
 * Whether the brand is worth asking for at all: only an account whose colour could be applied, so a
 * signed-out visitor or a lower tier never spends a request on every page load.
 */
export function shouldLoadAccountAppColor({authenticated, plan}: Pick<AccountAppColorInput, "authenticated" | "plan">): boolean {
	return authenticated && canUseCustomerBranding(plan);
}

export interface AccountAppColorInput {
	/** The auth listener confirmed a signed-in session (not a stored User from the last visit). */
	authenticated: boolean;
	plan: Plan;
	primaryColor: string | null;
	/** The current route, raw (locale prefix included). */
	pathname: string;
}
